import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Heart, Music, Lock, Sparkles } from "lucide-react";

export default function WishlistFeed() {
  const { data } = useQuery({ queryKey: ["wishes"], queryFn: () => api.get("/wishes").then(r => r.data) });
  if (!data) return null;

  // Backward compat: if backend still returns raw list, wrap it
  const envelope = Array.isArray(data) ? { locked: false, wishes: data, count: data.length, threshold: 25 } : data;
  const { locked, count = 0, threshold = 25, wishes = [], top_genres = [], admin_preview } = envelope;

  if (locked) {
    return (
      <div className="mt-14" data-testid="home-wishes-locked">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Heart size={12} className="text-pink-400" /> Fælles ønskeliste</div>
            <h2 className="font-display font-bold text-3xl mt-1">Hvem drømmer vi om?</h2>
          </div>
          <div className="text-xs text-zinc-500 tabular-nums">{count} / {threshold} ønsker</div>
        </div>
        <div className="card-flat p-8 flex items-center gap-5 relative overflow-hidden">
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-pink-500/10 blur-3xl" aria-hidden="true" />
          <div className="h-14 w-14 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0">
            <Lock size={20} className="text-zinc-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white">Ønskelisten åbner ved 25 ønsker</div>
            <p className="text-sm text-zinc-400 mt-1">Fælles ønskeliste vises, når spillerne tilsammen har afgivet mindst 25 topartist-ønsker.</p>
            {/* progress bar */}
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-pink-400" style={{ width: `${Math.min(100, (count / threshold) * 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (wishes.length === 0) return null;

  const top = wishes[0];
  const totalWishers = wishes.reduce((s, w) => s + w.count, 0);

  return (
    <div className="mt-14" data-testid="home-wishes">
      <div className="flex items-baseline justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Heart size={12} className="text-pink-400" /> Fælles ønskeliste</div>
          <h2 className="font-display font-bold text-3xl mt-1">Hvem drømmer vi om?</h2>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          {admin_preview && (
            <span className="px-2 py-1 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-300 font-semibold" data-testid="wishes-admin-preview">
              ADMIN PREVIEW
            </span>
          )}
          <span>{totalWishers} ønsker fra fællesskabet</span>
        </div>
      </div>

      {/* Top spotlight card */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mb-4">
        <div className="card-flat p-6 flex items-center gap-5 relative overflow-hidden" data-testid="wishes-spotlight">
          <div className="absolute -right-12 -top-12 h-52 w-52 rounded-full bg-pink-500/10 blur-3xl" aria-hidden="true" />
          <div className="label-eyebrow absolute top-4 right-5 flex items-center gap-1 text-pink-300"><Sparkles size={10} /> Mest ønsket</div>
          {top.artist.image_url ? (
            <img src={top.artist.image_url} alt="" className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
          ) : <div className="w-24 h-24 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0"><Music size={24} /></div>}
          <div className="min-w-0 flex-1">
            <div className="text-xl font-display font-bold truncate">{top.artist.name}</div>
            <div className="text-sm text-zinc-500 mt-1">
              <span className="font-score text-2xl text-white">{top.count}</span> spiller{top.count === 1 ? "" : "e"} drømmer om dette
            </div>
            <div className="mt-3 flex -space-x-2">
              {top.wishers.slice(0, 8).map(u => (
                <Link to={`/u/${u.user_id}`} key={u.user_id} title={u.display_name}>
                  {u.effective_picture ? (
                    <img src={u.effective_picture} alt="" className="w-6 h-6 rounded-full border-2 border-black object-cover" />
                  ) : <div className="w-6 h-6 rounded-full bg-zinc-700 border-2 border-black" />}
                </Link>
              ))}
              {top.count > 8 && <span className="text-xs text-zinc-500 pl-3 self-center">+{top.count - 8}</span>}
            </div>
          </div>
        </div>

        {/* Top genres */}
        <div className="card-flat p-6" data-testid="wishes-top-genres">
          <div className="label-eyebrow mb-3">Genrer i toppen</div>
          {top_genres.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {top_genres.map((g, i) => (
                <span key={g.genre} className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
                  <span className="text-zinc-500 mr-1">{i + 1}</span>
                  <span className="text-zinc-200">{g.genre}</span>
                  <span className="text-zinc-500 ml-2">{g.count}</span>
                </span>
              ))}
            </div>
          ) : <div className="text-sm text-zinc-500">Ingen genre-data endnu.</div>}
        </div>
      </div>

      {/* Compact runners-up rows */}
      <div className="card-flat overflow-hidden">
        {wishes.slice(1, 6).map((w, i) => (
          <div key={w.artist.artist_id} className={`flex items-center gap-4 px-5 py-3 ${i < Math.min(wishes.length - 1, 5) - 1 ? "border-b border-white/5" : ""}`} data-testid={`wish-${w.artist.artist_id}`}>
            <div className="font-score text-lg text-zinc-500 w-6 text-center">{i + 2}</div>
            {w.artist.image_url ? (
              <img src={w.artist.image_url} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />
            ) : <div className="w-10 h-10 rounded-md bg-zinc-800 flex items-center justify-center flex-shrink-0"><Music size={14} /></div>}
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{w.artist.name}</div>
              <div className="text-xs text-zinc-500">{w.count} ønsker</div>
            </div>
            <div className="flex -space-x-1.5">
              {w.wishers.slice(0, 3).map(u => (
                <div key={u.user_id} title={u.display_name}>
                  {u.effective_picture ? (
                    <img src={u.effective_picture} alt="" className="w-5 h-5 rounded-full border-2 border-black object-cover" />
                  ) : <div className="w-5 h-5 rounded-full bg-zinc-700 border-2 border-black" />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
